import React, { useEffect, useState } from 'react';
import Toolbar from '@mui/material/Toolbar';
import {
  DataGrid,
  GridActionsCellItem,
  GridColDef,
  GridRowId,
  GridRowModel,
  GridRowModes,
  GridRowModesModel,
  GridRowParams,
  GridRowsProp,
  GridToolbarContainer,
  MuiBaseEvent,
  MuiEvent,
} from '@mui/x-data-grid';
import AddIcon from '@mui/icons-material/Add';
import CancelIcon from '@mui/icons-material/Cancel';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import SaveIcon from '@mui/icons-material/Save';
import { Button, Paper, Typography } from '@mui/material';

import { useAppDispatch, useAppSelector } from '../app/hooks';
import '../App.css';
import {
  newTerm, fetchTerms, createTerm, updateTerm, deleteTerm, Term,
} from '../features/terms/terms';
import { RootState } from '../app/store';

interface EditToolbarProps {
  setRows: (newRows: (oldRows: GridRowsProp<Term>) => GridRowsProp<Term>) => void;
  setRowModesModel: (
    newModel: (oldModel: GridRowModesModel) => GridRowModesModel
  ) => void;
}

const AddToolbar = ({ setRows, setRowModesModel }: EditToolbarProps) => {
  const handleClick = () => {
    const term = newTerm();
    setRows((oldRows) => [...oldRows, term]);

    setRowModesModel((oldModel) => ({
      ...oldModel,
      [term.id]: { mode: GridRowModes.Edit, fieldToFocus: 'id' },
    }));
  };

  return (
    <GridToolbarContainer>
      <Button color="primary" startIcon={<AddIcon />} onClick={handleClick}>
        Add Term
      </Button>
    </GridToolbarContainer>
  );
};

const Terms = () => {
  const termsState = useAppSelector((state: RootState) => state.terms);
  const dispatch = useAppDispatch();
  const [rowModesModel, setRowModesModel] = useState<GridRowModesModel>({});
  const [rows, setRows] = useState(termsState.value);

  useEffect(() => { dispatch(fetchTerms()); }, []);
  useEffect(() => setRows(termsState.value), [termsState.value]);

  const handleRowEditStart = (
    params: GridRowParams,
    event: MuiEvent<React.SyntheticEvent>,
  ) => {
    event.defaultMuiPrevented = true;
  };

  const handleRowEditStop = (params: GridRowParams, event: MuiEvent<MuiBaseEvent>) => {
    event.defaultMuiPrevented = true;
  };

  const handleEditClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.Edit } });
  };

  const handleSaveClick = (id: GridRowId) => () => {
    setRowModesModel({ ...rowModesModel, [id]: { mode: GridRowModes.View } });
  };

  const handleDeleteClick = (id: GridRowId) => () => {
    const rowToDelete = rows.find((row) => row.id === id);
    if (rowToDelete) {
      setRows(rows.filter((row) => row.id !== id));
      dispatch(deleteTerm(rowToDelete));
    }
  };

  const handleCancelClick = (id: GridRowId) => () => {
    setRowModesModel({
      ...rowModesModel,
      [id]: { mode: GridRowModes.View, ignoreModifications: true },
    });
  };

  const processRowUpdate = (newRow: GridRowModel<Term>) => {
    const oldRowIdx = rows.findIndex((r) => r.id === newRow.id);
    if (oldRowIdx < 0) {
      setRows(rows.map((r) => (r.id === '' ? newRow : r)));
      dispatch(createTerm(newRow));
    } else {
      setRows([...rows.slice(0, oldRowIdx), newRow, ...rows.slice(oldRowIdx + 1)]);
      dispatch(updateTerm(newRow));
    }
    return newRow;
  };

  const columns: GridColDef<Term>[] = [
    {
      field: 'id',
      headerName: 'Id',
      editable: true,
    },
    {
      field: 'name',
      headerName: 'Name',
      editable: true,
      width: 200,
    },
    {
      field: 'rsp',
      headerName: 'RSP',
      editable: true,
    },
    {
      field: 'description1',
      headerName: 'Description 1',
      editable: true,
      width: 700,
    },
    {
      field: 'description2',
      headerName: 'Description 2',
      editable: true,
      width: 200,
    },
    {
      field: 'description3',
      headerName: 'Description 3',
      editable: true,
      width: 200,
    },
    {
      field: 'mode',
      headerName: 'Mode',
      type: 'string',
      editable: true,
      width: 200,
    },
    {
      field: 'cost',
      headerName: 'Cost',
      editable: true,
      width: 80,
    },
    {
      field: 'actions',
      type: 'actions',
      width: 80,
      getActions: (params) => {
        const isInEditMode = rowModesModel[params.row.id]?.mode === GridRowModes.Edit;

        if (isInEditMode) {
          return [<GridActionsCellItem
            icon={<SaveIcon />}
            label="Save"
            onClick={handleSaveClick(params.row.id)}
          />,
            <GridActionsCellItem
              icon={<CancelIcon />}
              label="Cancel"
              className="textPrimary"
              onClick={handleCancelClick(params.row.id)}
              color="inherit"
            />,
          ];
        }

        return [
          <GridActionsCellItem
            icon={<EditIcon />}
            label="Edit"
            className="textPrimary"
            onClick={handleEditClick(params.row.id)}
            color="inherit"
          />,
          <GridActionsCellItem
            icon={<DeleteIcon />}
            label="Delete"
            onClick={handleDeleteClick(params.row.id)}
          />,
        ];
      },
    },
  ];

  return (
    <Paper
      elevation={0}
      sx={{
        p: 2, mr: 15, ml: 15, mt: 10,
      }}
    >
      <Toolbar disableGutters sx={{ mb: 1.5, display: 'flex', justifyContent: 'space-between' }}>
        <Typography variant="h6">Terms</Typography>
      </Toolbar>
      <DataGrid
        autoHeight
        loading={termsState.status === 'loading'}
        rows={rows}
        columns={columns}
        initialState={{
          sorting: {
            sortModel: [{ field: 'speed', sort: 'asc' }],
          },
        }}
        editMode="row"
        rowModesModel={rowModesModel}
        pageSizeOptions={[100]}
        onRowEditStart={handleRowEditStart}
        onRowEditStop={handleRowEditStop}
        processRowUpdate={processRowUpdate}
        onProcessRowUpdateError={(error) => console.error(error)}
        components={{
          Toolbar: AddToolbar,
        }}
        componentsProps={{
          toolbar: { setRows, setRowModesModel },
        }}
      />
    </Paper>
  );
};

export default Terms;
